import { getIdentifier, resolveId } from "@medplum/core";
import { QuestionnaireResponse, Task } from "@medplum/fhirtypes";

import { formatISO } from "date-fns";

import { Systems } from "@/fhir/enums/systems";
import { AppDispatch } from "@/store";
import { setDeliveryDate, setPostpartumAge } from "@/store/slices/pregnancy";
import { middleOfDay } from "@/utils/datetime";

import { getGtpalDeliveryDatesByIdentifier } from "../services/postpartum";
import { PregnancyRecord } from "../types";
import { postpartumWeeksAndDaysAt } from "../utils/postpartum";

/**
 * Finds the pregnancy List ID that references the given postpartum-tracker Task.
 *
 * @param pregnancyRecords - Patient pregnancy records keyed by list ID
 * @param taskId - FHIR Task ID
 * @returns Matching list ID, or `undefined` when no record references the task
 */
export function findPregnancyListIdForPostpartumTask(
  pregnancyRecords: Record<string, PregnancyRecord>,
  taskId: string,
): string | undefined {
  return Object.entries(pregnancyRecords).find(([, record]) =>
    record.list.entry?.some((entry) => resolveId(entry.item) === taskId),
  )?.[0];
}

/**
 * Writes delivery date and postpartum age snapshot onto a pregnancy record in Redux.
 *
 * @param dispatch - Redux dispatch function
 * @param patientId - FHIR Patient ID
 * @param listId - Pregnancy record List ID
 * @param deliveryDate - Delivery/close-out date as ISO string or Date
 */
export function dispatchPostpartumForDeliveryDate(
  dispatch: AppDispatch,
  patientId: string,
  listId: string,
  deliveryDate: string | Date,
): void {
  const normalizedDate = middleOfDay(deliveryDate)!;
  const deliveryDateISO = formatISO(normalizedDate);
  const postpartumAge = postpartumWeeksAndDaysAt(deliveryDateISO);

  dispatch(setDeliveryDate({ patientId, listId, deliveryDate: deliveryDateISO }));
  dispatch(
    setPostpartumAge({
      patientId,
      listId,
      postpartumAge: postpartumAge
        ? { weeks: postpartumAge.weeks, days: postpartumAge.days }
        : undefined,
    }),
  );
}

/**
 * Updates pregnancy-slice postpartum fields after a successful GTPAL delivery-date sync.
 *
 * @param dispatch - Redux dispatch function
 * @param patientId - FHIR Patient ID
 * @param pregnancyRecords - Current Redux pregnancy records for the patient
 * @param questionnaireResponse - Saved GTPAL QuestionnaireResponse
 * @param updatedTasks - Postpartum-tracker Tasks returned from the sync
 */
export function dispatchPostpartumFromGtpalSave(
  dispatch: AppDispatch,
  patientId: string,
  pregnancyRecords: Record<string, PregnancyRecord>,
  questionnaireResponse: QuestionnaireResponse,
  updatedTasks: Task[],
): void {
  const deliveryDatesByIdentifier = getGtpalDeliveryDatesByIdentifier(questionnaireResponse);

  for (const task of updatedTasks) {
    const taskId = task.id;
    if (!taskId) continue;

    const listId = findPregnancyListIdForPostpartumTask(pregnancyRecords, taskId);
    if (!listId) continue;

    const identifier = getIdentifier(task, Systems.IdentifierType);
    const deliveryDate =
      task.executionPeriod?.start ??
      (identifier ? deliveryDatesByIdentifier[identifier] : undefined);

    if (!deliveryDate) continue;

    dispatchPostpartumForDeliveryDate(dispatch, patientId, listId, deliveryDate);
  }
}
